import { Card, FormGroup, InputWithUnit } from '../ui/Card';
import { fmt, fmtDollarInt } from '../../lib/format';
import type { CalculatorInputs, CalculatorResults } from '../../lib/types';

interface Props {
  inputs: CalculatorInputs;
  results: CalculatorResults;
  updateInput: <K extends keyof CalculatorInputs>(key: K, value: CalculatorInputs[K]) => void;
  cashOnHand?: number;
}

export function CashToCloseCard({ inputs, results, updateInput, cashOnHand }: Props) {
  const { cashToClose } = results;

  return (
    <Card title="Estimated Cash to Close">
      <ul className="ctc-list">
        {cashToClose.items.map((item, i) => {
          if (item.type === 'divider') return null;
          const isCredit = item.type === 'credit';
          return (
            <li key={i} className={isCredit ? 'ctc-credit' : ''}>
              <span className="ctc-label">{item.label}</span>
              <span className="ctc-value">
                {isCredit ? '-' : ''}${fmt(Math.abs(item.value))}
              </span>
            </li>
          );
        })}
        <li className="ctc-total">
          <span className="ctc-label">Cash Needed at Closing</span>
          <span className="ctc-value">${fmt(Math.max(0, cashToClose.cashToClose))}</span>
        </li>
        {cashOnHand !== undefined && cashOnHand > 0 && (() => {
          const needed = Math.max(0, cashToClose.cashToClose);
          const gap = needed - cashOnHand;
          const isShort = gap > 0;
          return (
            <>
              <li style={{ borderTop: '1px dashed var(--border)', marginTop: '0.4rem', paddingTop: '0.4rem' }}>
                <span className="ctc-label" style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Profile Cash on Hand</span>
                <span className="ctc-value" style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>{fmtDollarInt(cashOnHand)}</span>
              </li>
              <li>
                <span className="ctc-label" style={{ fontWeight: 700, color: isShort ? 'var(--red)' : '#16a34a' }}>
                  {isShort ? '⚠️ Shortfall' : '✅ Surplus'}
                </span>
                <span className="ctc-value" style={{ fontWeight: 700, color: isShort ? 'var(--red)' : '#16a34a' }}>
                  {isShort ? '+' : '-'}{fmtDollarInt(Math.abs(gap))}
                </span>
              </li>
            </>
          );
        })()}
      </ul>

      <div className="ctc-adjust-row">
        <FormGroup label="Closing Cost %" hint="MN typical: 2.5–3.5% of loan">
          <InputWithUnit
            unit="%"
            unitPosition="right"
            value={inputs.closingCostPct}
            onChange={v => updateInput('closingCostPct', v)}
            min={0}
            max={10}
            step={0.25}
          />
        </FormGroup>
        <FormGroup label="Seller Concession" hint="Seller-paid closing costs">
          <InputWithUnit
            unit="$"
            value={inputs.sellerConcession}
            onChange={v => updateInput('sellerConcession', v)}
            min={0}
            step={500}
          />
        </FormGroup>
        <FormGroup label="Prepaid Items (est.)" hint="Leave 0 to auto-estimate">
          <InputWithUnit
            unit="$"
            value={inputs.prepaids}
            onChange={v => updateInput('prepaids', v)}
            min={0}
            step={100}
          />
        </FormGroup>
      </div>
      <div className="note-box" style={{ marginTop: '0.75rem' }}>
        Prepaids typically include ~3 months of property tax, 14 months of homeowner's insurance,
        and prepaid interest through month-end. Auto-estimated when left at $0.
      </div>
    </Card>
  );
}
